import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { useSelector } from "react-redux";
import {
  buildDailyHoursViewMailPlainText,
  buildDailyHoursViewMailSubject,
} from "../../../app/utils/common/dailyHoursMailto";
import { sendDailyHoursViewEmail } from "../../../app/utils/common/dailyHoursEmail";
import {
  checkPermission,
  hasAdminAccess,
  permissionType,
  splitDailyHoursNotifyEmails,
} from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import "./DailyHoursSendMail.scss";

const EMPTY_DAILY_EXTRAS = {
  inProgressTasks: "",
  plannedForTomorrow: "",
  issueBlocker: "",
  dependency: "",
  supportRequiredFrom: "",
  meetingsDiscussions: "",
  clientFeedback: "",
  deploymentNotes: "",
};

const isValidEmailList = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return true;
  return splitDailyHoursNotifyEmails(raw).every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

export function mapTaskDetailsForDailyHoursMail(taskDetails = []) {
  return taskDetails.map((task) => {
    const totalMinutes = Number(task.duration) || 0;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      selectedProject: {
        value: task.projectId,
        label: task.projectName || `Project #${task.projectId}`,
      },
      prjCategory: {
        value: task.projectCategory,
        label: task.projectCategory,
      },
      durationHours: hours > 0 ? { value: String(hours) } : null,
      durationMinutes: minutes > 0 ? { value: String(minutes) } : null,
      taskDescription: task.description || "",
    };
  });
}

const DailyHoursSendMail = ({
  date,
  userDetails,
  taskDetails = [],
  dailyExtras,
  className = "",
}) => {
  const { user } = useSelector((state) => state.auth.auth);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [sendingViewEmail, setSendingViewEmail] = useState(false);
  const [mailDraft, setMailDraft] = useState({ to: "", subject: "", body: "", cc: "", bcc: "" });
  const mailDraftRef = useRef({ to: "", subject: "", body: "", cc: "", bcc: "" });

  const canSendViewEmail = checkPermission(permissionType.DAILY_HOURS, "read users");
  const canSendDailyHoursMail =
    hasAdminAccess(user) || checkPermission(permissionType.DAILY_HOURS, "send mail");

  const adminName = useMemo(() => {
    const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim();
    return name || user?.email || "Admin";
  }, [user]);

  const employeeUserId = userDetails?.userId ?? null;

  const employeeName = useMemo(() => {
    if (!userDetails) return "Employee";
    const name = [userDetails.firstname, userDetails.lastname].filter(Boolean).join(" ").trim();
    return name || "Employee";
  }, [userDetails]);

  const employeeNotifyEmail = useMemo(() => {
    const emails = splitDailyHoursNotifyEmails(userDetails?.dailyHoursNotifyEmail);
    return emails.join(", ");
  }, [userDetails?.dailyHoursNotifyEmail]);

  const mailTasks = useMemo(
    () => mapTaskDetailsForDailyHoursMail(taskDetails),
    [taskDetails]
  );

  const mailDailyExtras = useMemo(
    () => ({ ...EMPTY_DAILY_EXTRAS, ...(dailyExtras || {}) }),
    [dailyExtras]
  );

  const showSendMailButton =
    canSendViewEmail && canSendDailyHoursMail && employeeUserId && date && mailTasks.length > 0;

  useEffect(() => {
    mailDraftRef.current = mailDraft;
  }, [mailDraft]);

  const openSendMailDialog = useCallback(() => {
    setMailDraft({
      to: employeeNotifyEmail,
      subject: buildDailyHoursViewMailSubject({ date }),
      body: buildDailyHoursViewMailPlainText({
        tasks: mailTasks,
        dailyExtras: mailDailyExtras,
        adminName,
      }),
      cc: "",
      bcc: "",
    });
    setShowSendMailModal(true);
  }, [date, mailTasks, mailDailyExtras, adminName, employeeNotifyEmail]);

  const resetSendMailDialog = useCallback(() => {
    setShowSendMailModal(false);
    setMailDraft({ to: "", subject: "", body: "", cc: "", bcc: "" });
  }, []);

  const handleSendViewEmail = useCallback(async () => {
    if (!employeeUserId || !date || !mailTasks.length || sendingViewEmail) return;
    if (!canSendDailyHoursMail) {
      showErrorToast("You do not have permission to send daily work updates emails.");
      return;
    }

    const draft = mailDraftRef.current;
    if (!splitDailyHoursNotifyEmails(draft.to).length) {
      showErrorToast("At least one recipient email is required in To.");
      return;
    }
    if (!isValidEmailList(draft.to)) {
      showErrorToast("Enter valid To emails separated by commas.");
      return;
    }
    if (!draft.subject.trim() || !draft.body.trim()) {
      showErrorToast("Email subject and message are required.");
      return;
    }
    if (!isValidEmailList(draft.cc)) {
      showErrorToast("Enter valid CC emails separated by commas.");
      return;
    }
    if (!isValidEmailList(draft.bcc)) {
      showErrorToast("Enter valid BCC emails separated by commas.");
      return;
    }

    setSendingViewEmail(true);
    try {
      await sendDailyHoursViewEmail({
        userId: employeeUserId,
        date,
        to: draft.to,
        subject: draft.subject,
        body: draft.body,
        emailSubject: draft.subject,
        emailBody: draft.body,
        cc: draft.cc,
        bcc: draft.bcc,
        senderName: adminName,
        sentBy: adminName,
      });
      showSuccessToast("Daily work updates email sent successfully.");
      resetSendMailDialog();
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to send daily work updates email.";
      showErrorToast(typeof message === "string" ? message : "Unable to send daily work updates email.");
    } finally {
      setSendingViewEmail(false);
    }
  }, [
    employeeUserId,
    date,
    mailTasks.length,
    sendingViewEmail,
    canSendDailyHoursMail,
    adminName,
    resetSendMailDialog,
  ]);

  if (!showSendMailButton) return null;

  return (
    <>
      <div className={`daily-hours-send-mail ${className}`.trim()}>
        <button
          type="button"
          className="daily-hours-send-mail__btn"
          onClick={openSendMailDialog}
        >
          Send mail
        </button>
        <p className="daily-hours-send-mail__hint">
          Sends a table-formatted update (same layout as submission )
        </p>
      </div>

      <Modal
        title="Send mail"
        open={showSendMailModal}
        onOk={handleSendViewEmail}
        onCancel={() => {
          if (sendingViewEmail) return;
          resetSendMailDialog();
        }}
        okText={sendingViewEmail ? "Sending…" : "Send email"}
        cancelText="Cancel"
        confirmLoading={sendingViewEmail}
        okButtonProps={{
          disabled:
            sendingViewEmail ||
            !splitDailyHoursNotifyEmails(mailDraft.to).length ||
            !mailDraft.subject.trim() ||
            !mailDraft.body.trim(),
        }}
        cancelButtonProps={{ disabled: sendingViewEmail }}
        width={760}
        className="invoice-mail-preview-modal"
      >
        <div className="invoice-mail-preview">
          <div className="invoice-mail-preview__meta">
            <div>
              <span className="invoice-mail-preview__label">To</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.to}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, to: event.target.value }))
                }
                placeholder="Recipient emails, comma separated"
              />
            </div>
            <div>
              <span className="invoice-mail-preview__label">CC</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.cc}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, cc: event.target.value }))
                }
                placeholder="Optional CC emails, comma separated"
              />
            </div>
            <div>
              <span className="invoice-mail-preview__label">BCC</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.bcc}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, bcc: event.target.value }))
                }
                placeholder="Optional BCC emails, comma separated"
              />
            </div>
            <div>
              <span className="invoice-mail-preview__label">Subject</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.subject}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, subject: event.target.value }))
                }
                placeholder="Email subject"
              />
            </div>
          </div>
          <div className="invoice-mail-preview__body">
            <textarea
              className="invoice-mail-preview__textarea"
              value={mailDraft.body}
              onChange={(event) =>
                setMailDraft((current) => ({ ...current, body: event.target.value }))
              }
              rows={12}
              placeholder="Email message"
            />
          </div>
          <div className="invoice-mail-preview__attachments">
            <span className="invoice-mail-preview__label">Content</span>
            <ul>
              <li>
                Table-formatted daily work updates for {employeeName} on {date}
              </li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DailyHoursSendMail;
